/**
 * Typed wrapper around window.api IPC calls for the output pipeline.
 * Centralizes preload API access; when preload is unavailable, returns gracefully.
 */

import type {
  SaveFileParams,
  SaveFileResponse,
  SaveSessionParams,
  SaveSessionResponse,
  SaveActivityLogsResponse,
  MarkMediaUploadedParams,
  EnqueueUploadParams,
  EnqueueUploadResponse,
  ActivityLogEntry
} from '@renderer/types/outputPipeline.types'

declare const window: Window & {
  api?: {
    saveFile?: (
      sessionId: string,
      fileName: string,
      fileType: 'photos' | 'videos' | 'gifs',
      data: ArrayBuffer,
      metadata?: Record<string, unknown>
    ) => Promise<SaveFileResponse>
    saveSession?: (sessionData: Record<string, unknown>) => Promise<{ success: boolean; error?: string }>
    saveActivityLogs?: (sessionId: string, logs: ActivityLogEntry[]) => Promise<{ success: boolean; error?: string }>
    getInternetStatus?: () => Promise<boolean>
    triggerSync?: () => Promise<{ success: boolean }>
    markMediaAsUploaded?: (
      sessionId: string,
      resourceId: string,
      resourceType: 'photo' | 'video' | 'gif',
      fileName: string
    ) => Promise<{ success: boolean; error?: string }>
    enqueueUpload?: (sessionId: string, resourceId: string) => Promise<EnqueueUploadResponse>
    enqueueSessionSync?: (sessionId: string) => Promise<{ success: boolean; error?: string }>
    markSessionUploaded?: (sessionId: string) => Promise<{ success: boolean; error?: string }>
  }
}

function getApi(): (typeof window)['api'] | undefined {
  if (typeof window === 'undefined') return undefined
  return window.api
}

/**
 * True when the full 2.2.2+ file-upload pipeline is available.
 * Used only in uploadQueue.ts to gate: saveFileToDB, markMediaUploaded, enqueueUpload.
 * Does NOT affect printing, devices, reprint, or any other window.api.* — those
 * remain available on all versions.
 */
export function hasPreloadAPI(): boolean {
  const api = getApi()
  return Boolean(api?.saveFile && api?.markMediaAsUploaded)
}

/**
 * True when the full 2.2.2+ session-upload pipeline is available.
 * Used only in stateUploader.ts to gate: saveSessionToDB, markSessionUploaded, enqueueSessionSync.
 * Does NOT affect other window.api.* (printing, devices, reprint, etc.).
 */
export function hasSessionPreloadAPI(): boolean {
  const api = getApi()
  return Boolean(api?.saveSession && api?.markSessionUploaded)
}

export async function checkNetworkStatus(): Promise<boolean> {
  const api = getApi()
  if (api?.getInternetStatus) {
    return await api.getInternetStatus()
  }
  return typeof navigator !== 'undefined' ? navigator.onLine : true
}

export async function saveFileToDB(params: SaveFileParams): Promise<SaveFileResponse> {
  const api = getApi()
  if (!api?.saveFile) {
    return { success: false, skipped: true, error: 'Preload API not available' }
  }
  try {
    return await api.saveFile(
      params.sessionId,
      params.fileName,
      params.fileType,
      params.data,
      params.metadata as Record<string, unknown> | undefined
    )
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error)
    }
  }
}

export async function markMediaUploaded(params: MarkMediaUploadedParams): Promise<{ success: boolean; error?: string }> {
  const api = getApi()
  if (!api?.markMediaAsUploaded) {
    return { success: true }
  }
  try {
    return await api.markMediaAsUploaded(
      params.sessionId,
      params.resourceId,
      params.resourceType,
      params.fileName
    )
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error)
    }
  }
}

export async function enqueueUpload(params: EnqueueUploadParams): Promise<EnqueueUploadResponse> {
  const api = getApi()
  if (!api?.enqueueUpload) {
    return { success: false, error: 'Preload API not available' }
  }
  try {
    return await api.enqueueUpload(params.sessionId, params.resourceId)
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error)
    }
  }
}

export async function saveSessionToDB(params: SaveSessionParams): Promise<SaveSessionResponse> {
  const api = getApi()
  if (!api?.saveSession) {
    return { success: false, error: 'Preload API not available' }
  }
  try {
    const payload: Record<string, unknown> = {
      id: params.id,
      projectId: params.projectId,
      userId: params.userId,
      timeline: params.timeline,
      userPreferences: params.userPreferences,
      userConsent: params.userConsent,
      devices: params.devices,
      transaction: params.transaction,
      gallery: params.gallery
    }
    if (params.eventId != null) payload.eventId = params.eventId
    if (params.userEmail != null) payload.userEmail = params.userEmail
    if (params.galleryKey != null) payload.galleryKey = params.galleryKey
    if (params.appSettingsTelemetryId != null) payload.appSettingsTelemetryId = params.appSettingsTelemetryId
    if (params.token != null) payload.token = params.token
    if (params.deviceId != null) payload.deviceId = params.deviceId
    return await api.saveSession(payload)
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error)
    }
  }
}

export async function markSessionUploaded(sessionId: string): Promise<{ success: boolean; error?: string }> {
  const api = getApi()
  if (!api?.markSessionUploaded) {
    return { success: true }
  }
  try {
    return await api.markSessionUploaded(sessionId)
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error)
    }
  }
}

export async function enqueueSessionSync(sessionId: string): Promise<{ success: boolean; error?: string }> {
  const api = getApi()
  if (!api?.enqueueSessionSync) {
    return { success: false, error: 'Preload API not available' }
  }
  try {
    return await api.enqueueSessionSync(sessionId)
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error)
    }
  }
}

export async function saveActivityLogsToDB(
  sessionId: string,
  logs: ActivityLogEntry[]
): Promise<SaveActivityLogsResponse> {
  const api = getApi()
  if (!api?.saveActivityLogs) {
    return { success: false, error: 'Preload API not available' }
  }
  try {
    return await api.saveActivityLogs(sessionId, logs)
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error)
    }
  }
}

export async function triggerSync(): Promise<{ success: boolean }> {
  const api = getApi()
  if (!api?.triggerSync) {
    return { success: false }
  }
  try {
    return await api.triggerSync()
  } catch {
    return { success: false }
  }
}
